/**
 * TicketStub — single landing card used by both the org-branded route
 * (/org/[slug]) and the squad-invite route (/wc-invite/[code]).
 *
 * Visual: stadium-night background with a foreground "stadium ticket" split
 * into a brand-coloured stub on top and a white info panel on the bottom.
 * Tapping the primary CTA plays a tear-off animation; once the top stub flies
 * off, the callers' onPress runs (typically navigates to sign-up).
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import {
  Animated,
  Easing,
  Platform,
  Pressable,
  ScrollView,
  Text,
  View,
} from 'react-native';
import { Image } from 'expo-image';
import { LinearGradient } from 'expo-linear-gradient';
import { ArrowRight, Check, Trophy } from 'lucide-react-native';
import Svg, { Polygon } from 'react-native-svg';
import { useTheme } from '../../../src/theme/useTheme';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { soundService } from '../../../src/shared/services/SoundService';
import { SOUND_IDS } from '../../../src/shared/constants/sounds.constants';
import { AvatarGroup } from '../../shared/AvatarGroup/AvatarGroup';
import { WEB_IMAGE_ALT_DECORATIVE } from '../../../src/shared/helpers/imageWebAlt.helper';
import {
  TICKET_STUB_COPY,
  TICKET_STUB_LAYOUT,
  TICKET_STUB_TEST_IDS,
  TICKET_STUB_TIMINGS,
  ACCENT_GOLD,
  ACCENT_GREEN,
  BARCODE_SEED,
  CONFETTI_PIECES,
} from './TicketStub.constants';
import { getTicketStubStyles, TICKET_STUB_PALETTE } from './TicketStub.styles';
import { buildTicketStubAvatarItems } from './ticketStubAvatars.helper';
import {
  getBodyTearPolygonPoints,
  getStubTearPolygonPoints,
} from './ticketStubJagged.helper';
import { formatLocksIn } from './ticketStubLockIn.helper';
import { detectMobileWebOs } from '../../../src/onboarding/helpers/detectMobileWebOs.helper';
import { getMobileStoreUrl } from '../../../src/onboarding/helpers/getMobileStoreUrl.helper';
import { openExternalUrl } from '../../../src/shared/helpers/openExternalUrl.helper';
import type { TicketStubProps } from './TicketStub.types';

const STUB_TEAR_POINTS = getStubTearPolygonPoints();
const BODY_TEAR_POINTS = getBodyTearPolygonPoints();

const AVATAR_STACK_VISIBLE = 3;

const fallbackInitial = (name: string): string => {
  const t = name.trim();
  return t.length > 0 ? t.charAt(0).toUpperCase() : '?';
};

export const TicketStub: React.FC<TicketStubProps> = ({
  brand,
  presentedBy,
  kicker,
  title,
  subtitle,
  memberCount,
  memberCountLabel,
  groupCode,
  matchCount,
  locksAtIso,
  locksPastLabel,
  primaryCta,
  disabledNotice,
  secondaryCta,
  showOpenInApp,
  contentBottomInset,
}) => {
  const theme = useTheme();
  const styles = useMemo(
    () =>
      getTicketStubStyles(theme, {
        brandPrimary: brand.primaryColor,
        brandSecondary: brand.secondaryColor,
        contentBottomInset,
      }),
    [theme, brand.primaryColor, brand.secondaryColor, contentBottomInset],
  );

  const tearProgress = useRef(new Animated.Value(0)).current;
  const [isTearing, setIsTearing] = useState(false);

  const openInStore = useCallback(() => {
    openExternalUrl(getMobileStoreUrl(detectMobileWebOs()));
  }, []);

  const runTearAndFire = useCallback(() => {
    if (isTearing || !primaryCta) return;
    setIsTearing(true);
    void soundService.play(SOUND_IDS.UI_PAPER_TEAR);
    Animated.timing(tearProgress, {
      toValue: 1,
      duration: TICKET_STUB_TIMINGS.TEAR_MS,
      // Non-overshoot ease-out — keeps `tearProgress` strictly in [0..1] so
      // every interpolation below lands on its final outputRange value and
      // stays there (instead of momentarily extrapolating back toward "card
      // is complete" while the spring overshoots past 1).
      easing: Easing.bezier(0.32, 0.72, 0.34, 1),
      useNativeDriver: true,
    }).start(() => {
      setTimeout(() => primaryCta.onPress(), TICKET_STUB_TIMINGS.REVEAL_HOLD_MS);
    });
  }, [isTearing, primaryCta, tearProgress]);

  // All interpolations use `extrapolate: 'clamp'` so values past the defined
  // input range hold at the last outputRange entry — defensive against any
  // floating-point drift past 1 and against future easings that might
  // overshoot.
  const stubTransform = useMemo(
    () => ({
      transform: [
        {
          translateY: tearProgress.interpolate({
            inputRange: [0, 1],
            outputRange: [0, TICKET_STUB_LAYOUT.STUB_FLY_Y],
            extrapolate: 'clamp',
          }),
        },
        {
          translateX: tearProgress.interpolate({
            inputRange: [0, 1],
            outputRange: [0, TICKET_STUB_LAYOUT.STUB_FLY_X],
            extrapolate: 'clamp',
          }),
        },
        {
          rotate: tearProgress.interpolate({
            inputRange: [0, 1],
            outputRange: ['0deg', `${TICKET_STUB_LAYOUT.STUB_FLY_ROTATE_DEG}deg`],
            extrapolate: 'clamp',
          }),
        },
      ],
      opacity: tearProgress.interpolate({
        inputRange: [0, 0.7, 1],
        outputRange: [1, 1, 0],
        extrapolate: 'clamp',
      }),
    }),
    [tearProgress],
  );

  const perfStyle = useMemo(
    () => ({
      opacity: tearProgress.interpolate({
        inputRange: [0, 0.4],
        outputRange: [1, 0],
        extrapolate: 'clamp',
      }),
    }),
    [tearProgress],
  );

  // Reveal crossfades in while the top stub is still flying off — both
  // animations share `tearProgress` with phased input ranges so they overlap
  // on a single timeline.
  const revealOpacity = useMemo(
    () =>
      tearProgress.interpolate({
        inputRange: [0, 0.25, 0.9, 1],
        outputRange: [0, 0, 1, 1],
        extrapolate: 'clamp',
      }),
    [tearProgress],
  );
  const revealTranslateY = useMemo(
    () =>
      tearProgress.interpolate({
        inputRange: [0, 0.9, 1],
        outputRange: [12, 0, 0],
        extrapolate: 'clamp',
      }),
    [tearProgress],
  );
  const checkScale = useMemo(
    () =>
      tearProgress.interpolate({
        inputRange: [0.3, 0.75, 1],
        outputRange: [0.5, 1.15, 1],
        extrapolate: 'clamp',
      }),
    [tearProgress],
  );
  const defaultContentOpacity = useMemo(
    () =>
      tearProgress.interpolate({
        inputRange: [0, 0.5, 1],
        outputRange: [1, 0, 0],
        extrapolate: 'clamp',
      }),
    [tearProgress],
  );

  /** Jagged edges flip on the instant the tear starts, then stay visible. */
  const tearEdgeOpacity = useMemo(
    () =>
      tearProgress.interpolate({
        inputRange: [0, 0.05, 1],
        outputRange: [0, 1, 1],
        extrapolate: 'clamp',
      }),
    [tearProgress],
  );

  const confettiPalette = useMemo(
    () => [
      brand.primaryColor,
      brand.secondaryColor ?? brand.primaryColor,
      ACCENT_GOLD,
      ACCENT_GREEN,
      '#ffffff',
    ],
    [brand.primaryColor, brand.secondaryColor],
  );

  const confettiAnims = useMemo(
    () =>
      CONFETTI_PIECES.map((piece, i) => ({
        tx: tearProgress.interpolate({
          inputRange: [0, 0.2, 1],
          outputRange: [0, 0, piece.dx],
          extrapolate: 'clamp',
        }),
        ty: tearProgress.interpolate({
          inputRange: [0, 0.2, 1],
          outputRange: [0, 0, piece.dy],
          extrapolate: 'clamp',
        }),
        rot: tearProgress.interpolate({
          inputRange: [0.2, 1],
          outputRange: ['0deg', `${piece.rotate}deg`],
          extrapolate: 'clamp',
        }),
        op: tearProgress.interpolate({
          inputRange: [0, 0.25, 0.4, 0.85, 1],
          outputRange: [0, 0, 1, 1, 0],
          extrapolate: 'clamp',
        }),
        color: confettiPalette[i % confettiPalette.length],
      })),
    [tearProgress, confettiPalette],
  );

  const avatarItems = useMemo(
    () => buildTicketStubAvatarItems(brand.name, memberCount, AVATAR_STACK_VISIBLE),
    [brand.name, memberCount],
  );

  // Both stats are optional, and an absent one is dropped rather than defaulted: the card is
  // shown for competitions of very different sizes, and the wrong number is worse than none.
  const locksInLabel = useMemo(
    () => (locksAtIso ? formatLocksIn(locksAtIso, locksPastLabel ?? TICKET_STUB_COPY.LOCKS_PAST_LABEL) : null),
    [locksAtIso, locksPastLabel],
  );
  const matchCountLabel = typeof matchCount === 'number' && matchCount > 0 ? String(matchCount) : null;
  const hasStats = matchCountLabel !== null || locksInLabel !== null;

  const hoverWeb = Platform.select({ web: WEB_HOVER_TRANSITION_STYLE });
  const gradientColors = useMemo<[string, string]>(
    () => [`${brand.primaryColor}33`, TICKET_STUB_PALETTE.stadiumDarkDeep],
    [brand.primaryColor],
  );

  return (
    <View style={styles.root}>
      <LinearGradient
        colors={gradientColors}
        style={styles.backdrop}
        start={{ x: 0.5, y: 0 }}
        end={{ x: 0.5, y: 1 }}
      />
      <View style={styles.halo} pointerEvents="none" />

      <ScrollView contentContainerStyle={styles.scroll} showsVerticalScrollIndicator={false}>
        <View style={styles.frame}>
          <View style={styles.headerRow}>
            <Text style={styles.headerLabel}>{TICKET_STUB_COPY.HEADER_INVITED}</Text>
          </View>

          <View style={styles.ticketWrap}>
            <Animated.View style={[styles.stub, stubTransform]}>
              <LinearGradient
                colors={[
                  brand.primaryColor,
                  brand.secondaryColor ?? brand.primaryColor,
                ]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={styles.stubGradient}
              />
              <View style={styles.stubDecoCircleLg} pointerEvents="none" />
              <View style={styles.stubDecoCircleSm} pointerEvents="none" />

              <View style={styles.stubTopRow}>
                <View style={styles.brandMark}>
                  {brand.logoUrl ? (
                    <Image
                      source={{ uri: brand.logoUrl }}
                      style={styles.brandMarkLogo}
                      contentFit="cover"
                      alt={WEB_IMAGE_ALT_DECORATIVE}
                    />
                  ) : (
                    <Text style={styles.brandMarkInitial}>{fallbackInitial(brand.name)}</Text>
                  )}
                </View>
                <View>
                  {presentedBy ? (
                    <Text style={styles.stubPresentedBy}>{presentedBy}</Text>
                  ) : null}
                  <Text style={styles.stubKicker}>{kicker ?? TICKET_STUB_COPY.STUB_KICKER}</Text>
                </View>
              </View>

              <Text style={styles.stubTitle}>{title}</Text>

              <View style={styles.stubTrophyRow}>
                <Trophy size={16} color="rgba(255,255,255,0.9)" />
                <Text style={styles.stubTrophyText}>
                  Winner takes the {brand.name} Cup
                </Text>
              </View>

              <Animated.View
                style={[styles.stubTearEdge, { opacity: tearEdgeOpacity }]}
                pointerEvents="none"
              >
                <Svg
                  width="100%"
                  height="100%"
                  viewBox="0 0 100 100"
                  preserveAspectRatio="none"
                >
                  <Polygon points={STUB_TEAR_POINTS} fill={TICKET_STUB_PALETTE.stadiumDark} />
                </Svg>
              </Animated.View>
            </Animated.View>

            <Animated.View style={[styles.perf, perfStyle]} pointerEvents="none">
              <View style={styles.perfNotchLeft} />
              <View style={styles.perfNotchRight} />
              <View style={styles.perfDashed} />
              <View style={styles.perfHintWrap}>
                <Text style={styles.perfHint}>{TICKET_STUB_COPY.TEAR_HINT}</Text>
              </View>
            </Animated.View>

            <View style={styles.body}>
              <Animated.View
                style={[styles.bodyDefaultContent, { opacity: defaultContentOpacity }]}
                pointerEvents={isTearing ? 'none' : 'auto'}
              >
              <Text style={styles.bodySubtitle}>{subtitle}</Text>

              {hasStats ? (
                <View style={styles.statsGrid} testID={TICKET_STUB_TEST_IDS.stats}>
                  {matchCountLabel !== null ? (
                    <View style={styles.statCell} testID={TICKET_STUB_TEST_IDS.matches}>
                      <Text style={styles.statLabel}>{TICKET_STUB_COPY.STAT_MATCHES_LABEL}</Text>
                      <Text style={styles.statValue}>{matchCountLabel}</Text>
                    </View>
                  ) : null}
                  {locksInLabel !== null ? (
                    <View style={styles.statCell} testID={TICKET_STUB_TEST_IDS.locks}>
                      <Text style={styles.statLabel}>{TICKET_STUB_COPY.STAT_LOCKS_LABEL}</Text>
                      <Text style={styles.statValue}>{locksInLabel}</Text>
                    </View>
                  ) : null}
                </View>
              ) : null}

              <View style={styles.bodyMemberRow}>
                <AvatarGroup
                  avatars={avatarItems}
                  totalMemberCount={memberCount}
                  maxVisible={AVATAR_STACK_VISIBLE}
                  ringColor={TICKET_STUB_PALETTE.ticketWhite}
                  keyPrefix="ticket-stub-avatars"
                />
                <Text style={styles.bodyMemberText}>{memberCountLabel}</Text>
              </View>

              {primaryCta ? (
                <Pressable
                  onPress={runTearAndFire}
                  disabled={isTearing}
                  testID={primaryCta.testID}
                  style={({ pressed, hovered }) => [
                    styles.primaryButton,
                    hoverWeb,
                    hovered && Platform.OS === 'web' && styles.primaryButtonHover,
                    pressed && styles.primaryButtonPressed,
                  ]}
                >
                  <Text style={styles.primaryButtonText}>{primaryCta.label}</Text>
                  <ArrowRight size={18} color={theme.white} strokeWidth={2.5} />
                </Pressable>
              ) : disabledNotice ? (
                <Text style={styles.disabledNotice}>{disabledNotice}</Text>
              ) : null}

              {groupCode ? (
                <View style={styles.barcodeRow}>
                  <View style={styles.barcodeStripes}>
                    {BARCODE_SEED.map((w, i) => (
                      <View
                        key={`bar-${i}`}
                        style={[
                          styles.barcodeBar,
                          w === 1 ? styles.barcodeBarW1
                            : w === 2 ? styles.barcodeBarW2
                            : styles.barcodeBarW3,
                        ]}
                      />
                    ))}
                  </View>
                  <View style={styles.barcodeMeta}>
                    <Text style={styles.barcodeMetaLabel}>{TICKET_STUB_COPY.GROUP_CODE_LABEL}</Text>
                    <Text style={styles.barcodeMetaValue}>{groupCode.toUpperCase()}</Text>
                  </View>
                </View>
              ) : null}
              </Animated.View>

              {isTearing ? (
                <Animated.View
                  style={[
                    styles.bodyRevealOverlay,
                    { opacity: revealOpacity, transform: [{ translateY: revealTranslateY }] },
                  ]}
                  pointerEvents="none"
                >
                  <Animated.View
                    style={[styles.revealCheckCircle, { transform: [{ scale: checkScale }] }]}
                  >
                    <Check size={32} color={theme.white} strokeWidth={3} />
                  </Animated.View>
                  <Text style={styles.revealTitle}>
                    You&apos;re in the{'\n'}{brand.name} squad
                  </Text>
                  <Text style={styles.revealSubtitle}>
                    {memberCount + 1} {TICKET_STUB_COPY.REVEAL_SUFFIX}
                    {matchCountLabel !== null
                      ? ` · ${matchCountLabel} ${TICKET_STUB_COPY.REVEAL_MATCHES_SUFFIX}`
                      : ''}
                  </Text>
                  {confettiAnims.map((anim, i) => (
                    <Animated.View
                      key={`confetti-${i}`}
                      style={[
                        styles.confettiPiece,
                        {
                          // brand-derived — can't be pre-computed in StyleSheet.create
                          backgroundColor: anim.color,
                          opacity: anim.op,
                          transform: [
                            { translateX: anim.tx },
                            { translateY: anim.ty },
                            { rotate: anim.rot },
                          ],
                        },
                      ]}
                    />
                  ))}
                </Animated.View>
              ) : null}

              {/* Jagged top edge — last child so it stays on top of the reveal overlay. */}
              <Animated.View
                style={[styles.bodyTearEdge, { opacity: tearEdgeOpacity }]}
                pointerEvents="none"
              >
                <Svg
                  width="100%"
                  height="100%"
                  viewBox="0 0 100 100"
                  preserveAspectRatio="none"
                >
                  <Polygon points={BODY_TEAR_POINTS} fill={TICKET_STUB_PALETTE.stadiumDark} />
                </Svg>
              </Animated.View>
            </View>
          </View>

          {secondaryCta ? (
            <Pressable
              onPress={secondaryCta.onPress}
              testID={secondaryCta.testID}
              style={({ pressed, hovered }) => [
                styles.secondaryButton,
                hoverWeb,
                hovered && Platform.OS === 'web' && styles.secondaryButtonHover,
                pressed && styles.primaryButtonPressed,
              ]}
            >
              <Text style={styles.secondaryButtonText}>{secondaryCta.label}</Text>
            </Pressable>
          ) : null}

          {showOpenInApp ? (
            <Pressable
              onPress={openInStore}
              accessibilityRole="link"
              accessibilityLabel="Get the Zporti app"
              style={({ pressed, hovered }) => [
                styles.continueWithAppButton,
                hoverWeb,
                hovered && Platform.OS === 'web' && styles.continueWithAppButtonHover,
                pressed && styles.primaryButtonPressed,
              ]}
            >
              <Text style={styles.continueWithAppText}>
                {TICKET_STUB_COPY.CONTINUE_WITH_APP}
              </Text>
              <ArrowRight size={16} color="rgba(255,255,255,0.85)" />
            </Pressable>
          ) : null}
        </View>
      </ScrollView>
    </View>
  );
};
